import DataLossWarningModal from 'components/DataLossWarningModal';
import VehicleSelector from 'components/VehicleSelector';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Vehicle } from 'further-types/lp/vehicle';
import { useDisclosure } from 'further-ui/hooks';
import { realisation as rules } from 'further-ui/validations';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useState } from 'react';
import {
  Controller,
  UseFieldArrayReplace,
  useFormContext,
} from 'react-hook-form';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import DatePicker from 'further-ui/components/DatePicker';
import { defaultHoldingClassRealisation } from '../constants';

type RealisationFormFieldsProps = {
  selectedVehicle: Vehicle;
  assetOptions: Array<{ id: string; title: string }>;
  hasHoldingClassesAddedOnDeal: boolean;
  replace: UseFieldArrayReplace<rules.CreateRealisationFormValues>;
};

const RealisationFormFields: React.FC<RealisationFormFieldsProps> = ({
  selectedVehicle,
  assetOptions,
  hasHoldingClassesAddedOnDeal,
  replace,
}) => {
  const { control, getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();
  const { selectedVehicleId } = useLayoutContext();
  const { latestApprovedEqualisationDate } = useEqualisations(
    selectedVehicleId ?? '',
  );

  const removeModalDisclosure = useDisclosure();
  const [previousAssetId, setPreviousAssetId] = useState<string>('');

  const onAssetChange = () => {
    if (getValues('assetId') !== previousAssetId) {
      replace([defaultHoldingClassRealisation]);
    }
  };

  const setAssetName = () => {
    const assetName = assetOptions.find(
      (asset) => asset.id === getValues('assetId'),
    )?.title;
    setValue('assetName', assetName ?? '');
  };

  const handleAssetChange = () => {
    if (hasHoldingClassesAddedOnDeal) {
      setPreviousAssetId(getValues('assetId') ?? '');
      removeModalDisclosure.onOpen();
    } else {
      onAssetChange();
    }
  };

  const handleConfirmAssetChange = () => {
    onAssetChange();
    removeModalDisclosure.onClose();
  };

  const handleCancelAssetChange = () => {
    setValue('assetId', previousAssetId, {
      shouldDirty: true,
    });

    removeModalDisclosure.onClose();
  };

  return (
    <>
      <FormField size="md" label="Vehicle">
        <VehicleSelector selectedVehicle={selectedVehicle} />
      </FormField>
      <FormField size="md" label="Asset">
        <Controller
          name="assetId"
          control={control}
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <BasicSelect
              {...rest}
              value={value}
              onChange={(val) => {
                handleAssetChange();
                rest.onChange(val);
                setAssetName();
              }}
              fullWidth
              options={assetOptions.map((a) => ({
                value: a.id,
                label: a.title,
              }))}
              placeholder="Select asset"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField
        label="Realisation date"
        tooltip="Select a target date if the actual realisation date is unknown at the point of processing. This transaction can be edited after submission to include the actual completion date."
        size="md"
      >
        <Controller
          name="ledgerDate"
          control={control}
          render={({
            fieldState: { error },
            field: { ref, value, onChange, ...rest },
          }) => (
            <DatePicker
              error={!!error}
              helperText={error?.message?.toString()}
              value={value}
              fullWidth
              onChange={onChange}
              minDate={latestApprovedEqualisationDate}
              {...rest}
            />
          )}
        />
      </FormField>
      <DataLossWarningModal
        disclosure={removeModalDisclosure}
        onConfirm={handleConfirmAssetChange}
        onCancel={handleCancelAssetChange}
        message="Changing the asset will remove all holding classes including any new ones you've added to the deal. Do you want to continue?"
      />
    </>
  );
};

export default RealisationFormFields;
